/**
 * Compress and normalize CIN images for fast upload and consistent format.
 * Keeps size small so "Submit All Badges" with many staff stays under proxy limits (avoids 413).
 */
const DEFAULT_MAX = 1000;
const DEFAULT_QUALITY = 0.82;
const JPEG_TYPE = "image/jpeg";

function getFileFromBlob(blob, name = "cin.jpg") {
  if (blob instanceof File) return blob;
  return new File([blob], name, { type: JPEG_TYPE });
}

export default function compressCINImage(fileOrBlob, options = {}) {
  const maxWidth = options.maxWidth ?? DEFAULT_MAX;
  const maxHeight = options.maxHeight ?? DEFAULT_MAX;
  const quality = options.quality ?? DEFAULT_QUALITY;

  const file = fileOrBlob instanceof File
    ? fileOrBlob
    : new File([fileOrBlob], "cin.jpg", { type: JPEG_TYPE });

  const isImage =
    file.type === "image/jpeg" ||
    file.type === "image/png" ||
    file.type === "image/webp" ||
    file.type === "image/gif";

  if (!isImage) {
    return Promise.resolve(file);
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width <= maxWidth && height <= maxHeight && file.type === JPEG_TYPE && file.size < 300 * 1024) {
        resolve(file);
        return;
      }

      const scale = Math.min(maxWidth / width, maxHeight / height, 1);
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const name = file.name && /\.(jpe?g|png|webp|gif)$/i.test(file.name)
            ? file.name.replace(/\.[^.]+$/i, ".jpg")
            : "cin.jpg";
          resolve(getFileFromBlob(blob, name));
        },
        JPEG_TYPE,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Invalid or unsupported image"));
    };

    img.src = url;
  });
}
